<template>
  <div id="task_activity_graph">
    <ContainerInfoItem
      source="task_activity_graph"
      :title="title"
      :description="range?.label"
      height="fit-content"
    >
      <SkeletonLoader v-if="isLoading" :page="loaderPages.CHART" />

      <LineChart
        v-else-if="chartCategories && chartData"
        id="task_activity"
        primary-color="#D89435"
        :categories="chartCategories"
        :series-data="chartData"
      />

      <EmptyScreen
        v-else
        source="task_activity"
        text="No task activity data found!"
        :type="emptyScreenTypes.CHART_AND_TABLE"
      />
    </ContainerInfoItem>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch, watchEffect} from "vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { formatDateToMonthDay } from "@/src/utils/dateTimeUtils.js";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import LineChart from "@/src/components/charts/LineChart.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  title: { type: String, default: "Task activity" },
});

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const chartCategories = ref(null);
const chartData = ref(null);
const isLoading = ref(false);

const seriesColors = {
  "Total Tasks": "#D89435",
  "Pending Tasks": "#2579EF",
  "Completed Tasks": "#48AF7D",
  "Overdue Tasks": "#AC1C1E",
};

const processTaskActivityData = (data) => {
  if (!data || !data.length) {
    return { categories: null, seriesData: null };
  }

  const sortedData = [...data].sort(
    (a, b) => new Date(a.date) - new Date(b.date),
  );

  const categories = sortedData.map((item) => formatDateToMonthDay(item.date));

  const seriesData = [
    {
      name: "Total Tasks",
      data: sortedData.map((item) => item.total_task),
      color: seriesColors["Total Tasks"],
    },
    {
      name: "Pending Tasks",
      data: sortedData.map((item) => item.pending_task),
      color: seriesColors["Pending Tasks"],
    },
    {
      name: "Completed Tasks",
      data: sortedData.map((item) => item.completed_task),
      color: seriesColors["Completed Tasks"],
    },
    {
      name: "Overdue Tasks",
      data: sortedData.map((item) => item.overdue_task),
      color: seriesColors["Overdue Tasks"],
    },
  ];

  return { categories, seriesData };
};

const fetchTaskActivity = async () => {
  try {
    isLoading.value = true;

    const params = {
      start_time: range.value?.start,
      end_time: range.value?.end,
      user_id: userIds.value,
      team_id: teamIds.value,
      sequence_id: filters.value?.sequence?.id,
    };

    const response = await AnalyticService.V2.fetchSequenceTaskEngagement(params);

    if (response?.success && response?.data) {
      const data = response?.data;

      const { categories, seriesData } = processTaskActivityData(data);

      chartCategories.value = categories;
      chartData.value = seriesData;
    } else {
      chartCategories.value = null;
      chartData.value = null;
    }
  } catch (error) {
    console.error("An error occurred while fetching task activity: ", error);
    chartCategories.value = null;
    chartData.value = null;
  } finally {
    isLoading.value = false;
  }
};

watch([range, filters, userIds, teamIds], () => {
  if ((range.value && userIds.value && teamIds.value) || (filters.value?.sequence?.id)) {
    fetchTaskActivity();
  }
}, { deep: true });


onMounted(() => {
  // fetchTaskActivity();
});

// useAnalyticsRefresh(fetchTaskActivity, [range, userIds, teamIds]);
</script>
